import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from 'features/common/components/Table';
import {
	COLUMN_MAX_WIDTH,
	COLUMN_MIN_WIDTH,
	RESEND_EMAIL_COLUMN_MAX_WIDTH,
	RESEND_EMAIL_COLUMN_MIN_WIDTH,
} from './constants';
import { ResponsesData, TableBodyProps } from 'features/CustomFieldsResponses/types';
import { downloadTicketQR, formatDate } from './utils';
import { useResendEmail } from 'features/CustomFieldsResponses/hooks/useEventCustomFields';

import { DataRow } from './ResponsesTable.styles';

export const TableBody: FC<TableBodyProps> = ({
	responsesQueryObject,
	questions,
	isAttendeesList,
	showDownloadQR = false,
	columnsN = 0,
	gridColumns = `repeat(${columnsN}, 1fr)`,
	eventMode,
}) => {
	const { t } = useTranslation();
	const { data: responses } = responsesQueryObject;
	const { resendEmail, isLoading: isResendingEmail } = useResendEmail();

	const renderCell = (value: string | number, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth}>
			{value}
		</Table.Cell>
	);

	const renderDownloadQRCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.can_download_qr ? (
				<Table.Button
					onClick={() => downloadTicketQR(data.ticket_id, data.email)}
					text={t('download')}
					category="outlined"
					className="small"
				/>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderResendEmailCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.is_buyer ? (
				<Table.Button
					onClick={() => resendEmail(data.order_id)}
					text={t('resend')}
					category="outlined"
					className="small"
					disabled={isResendingEmail}
				/>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderScannedCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.is_validated ? (
				<span style={{ color: '#10b981', fontWeight: 600 }}>
					✓ Scanned {data.scan_count && data.scan_count > 1 ? `(${data.scan_count}x)` : ''}
				</span>
			) : (
				<span style={{ color: '#6b7280' }}>Not Scanned</span>
			)}
		</Table.Cell>
	);

	const renderPromotionCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.promotion_used ? (
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					<span style={{ color: '#8b5cf6', fontWeight: 600 }}>{data.promotion_used.code}</span>
					<span style={{ fontSize: '12px', color: '#6b7280' }}>-${data.promotion_used.amount.toFixed(2)}</span>
				</div>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderGiftCardsCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.gift_cards_used && data.gift_cards_used.length > 0 ? (
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					{data.gift_cards_used.map((gc, idx) => (
						<div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '12px' }}>****{gc.last4}</span>
							<span style={{ fontSize: '11px', color: '#6b7280' }}>-${gc.applied_amount.toFixed(2)}</span>
						</div>
					))}
				</div>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderSeatCell = (seatId: string, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth}>
			<div
				style={{
					wordWrap: 'break-word',
					whiteSpace: 'normal',
					lineHeight: '1.4',
					maxHeight: '2.8em',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
				}}
			>
				{seatId}
			</div>
		</Table.Cell>
	);

	if (!responses) {
		return null;
	}

	return (
		<tbody>
			{responses.pages.map((page: ResponsesData[]) =>
				page.map((data: ResponsesData, index: number) => {
					const responses = data.responses.filter(
						(response) => !['ATTENDEE_NAME', 'ATTENDEE_EMAIL'].includes(response.custom_field.name)
					);

					// Extract seat_id from data directly or from tickets array
					const seatId =
						data.seat_id ||
						data.tickets
							?.map((ticket) => ticket.seat_id)
							.filter(Boolean)
							?.join(', ') ||
						'-';

					let cols = [];

					if (eventMode === 'normal') {
						cols = [
							<>
								{renderCell(data.amount.toFixed(2), COLUMN_MIN_WIDTH[5])}
								{renderCell(formatDate(data.date), COLUMN_MIN_WIDTH[6])}
								{renderCell(data.status, COLUMN_MIN_WIDTH[7])}
								{renderCell(data.ticket_types_string, COLUMN_MIN_WIDTH[8])}
								{renderScannedCell(data, COLUMN_MIN_WIDTH[9], COLUMN_MAX_WIDTH[9])}
								{renderPromotionCell(data, COLUMN_MIN_WIDTH[10], COLUMN_MAX_WIDTH[10])}
								{renderGiftCardsCell(data, COLUMN_MIN_WIDTH[11], COLUMN_MAX_WIDTH[11])}
								{renderSeatCell(seatId, COLUMN_MIN_WIDTH[12], COLUMN_MAX_WIDTH[12])}
							</>,
						];
					} else {
						cols = [
							<>
								{renderCell(formatDate(data.date), COLUMN_MIN_WIDTH[6])}
								{renderCell(data.ticket_types_string, COLUMN_MIN_WIDTH[8])}
								{renderScannedCell(data, COLUMN_MIN_WIDTH[9], COLUMN_MAX_WIDTH[9])}
								{renderPromotionCell(data, COLUMN_MIN_WIDTH[10], COLUMN_MAX_WIDTH[10])}
								{renderGiftCardsCell(data, COLUMN_MIN_WIDTH[11], COLUMN_MAX_WIDTH[11])}
								{renderSeatCell(seatId, COLUMN_MIN_WIDTH[12], COLUMN_MAX_WIDTH[12])}
							</>,
						];
					}

					cols = cols.concat(
						questions.map((question) => {
							const response = responses.find((response) => response.custom_field.name === question);
							let value = response?.string_value || '-';
							if (response && response.choice_id > 0) {
								value = response.choice.name;
							}
							return renderCell(value, COLUMN_MIN_WIDTH[5], COLUMN_MAX_WIDTH[5]);
						})
					);

					return (
						<DataRow gridColumns={gridColumns} key={[data.order_id, index].join('_')} textAlign="center">
							{renderCell(data.order_id, COLUMN_MIN_WIDTH[0])}
							{renderCell(data.is_buyer ? 'Buyer' : 'Attendee', COLUMN_MIN_WIDTH[1])}
							{renderCell(data.email || '-', COLUMN_MIN_WIDTH[2])}
							{renderCell(data.full_name || '-', COLUMN_MIN_WIDTH[3])}
							{renderCell(data.phone_number || '-', COLUMN_MIN_WIDTH[4])}

							{(isAttendeesList ? cols : cols.reverse()).map((el) => el)}
							{isAttendeesList && showDownloadQR && renderDownloadQRCell(data, COLUMN_MIN_WIDTH[9])}
							{renderResendEmailCell(data, RESEND_EMAIL_COLUMN_MIN_WIDTH, RESEND_EMAIL_COLUMN_MAX_WIDTH)}
						</DataRow>
					);
				})
			)}
		</tbody>
	);
};
